import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Dog, Stethoscope, Gift } from "lucide-react";
import { toast } from "sonner";
import AuthShell, { Field, inputClass } from "@/components/AuthShell";
import { PrimaryButton } from "@/components/kit";
import { useAuth } from "@/context/AuthContext";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [accepted, setAccepted] = useState(false);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters.");
    if (!accepted) return toast.error("Please accept the Terms, Privacy Policy and Disclaimer.");
    setLoading(true);
    try {
      const user = await signup(email.trim(), password, name.trim(), role, accepted, { ref });
      navigate(user.role === "vet" ? "/app/vet-onboarding" : "/app", { replace: true });
    } catch (err) {
      toast.error(err.message || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { key: "user", label: "Pet parent", icon: Dog },
    { key: "vet", label: "Veterinary clinic", icon: Stethoscope },
  ];

  return (
    <AuthShell
      title="Create your account"
      subtitle="Start caring smarter in under a minute."
      footer={
        <>
          Already have an account?{" "}
          <Link to="/login" className="font-bold text-[#E86A45]">Log in</Link>
        </>
      }
    >
      <form onSubmit={submit}>
        {ref && (
          <div className="flex items-center gap-3 mb-4 p-3 rounded-2xl bg-[#E8F0E5] border border-[#cfe0c9]" data-testid="signup-invite-banner">
            <Gift size={20} color="#6F8F72" className="shrink-0" />
            <p className="text-sm font-semibold text-[#4b6b4e]">
              You were invited! Sign up and you both get <b>30 days of Wooflect Pro</b> free.
            </p>
          </div>
        )}
        <Field label="I am a…">
          <div className="grid grid-cols-2 gap-3">
            {roles.map((r) => (
              <button
                type="button"
                key={r.key}
                data-testid={`signup-role-${r.key}`}
                onClick={() => setRole(r.key)}
                className={`flex items-center gap-2 px-4 py-3 rounded-2xl border-2 font-bold transition-all ${
                  role === r.key
                    ? "border-[#E86A45] bg-[#FFF7F3] text-[#B54828]"
                    : "border-[#E8E3DA] text-[#4A4746] hover:border-[#C4BFB8]"
                }`}
              >
                <r.icon size={18} /> {r.label}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Name">
          <input
            data-testid="signup-name-input"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={role === "vet" ? "Clinic contact name" : "Your name"}
            className={inputClass}
          />
        </Field>
        <Field label="Email">
          <input
            data-testid="signup-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className={inputClass}
          />
        </Field>
        <Field label="Password">
          <div className="relative">
            <input
              data-testid="signup-password-input"
              type={show ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              className={inputClass + " pr-12"}
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9A938B] hover:text-[#4A4746]"
            >
              {show ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </Field>

        <label className="flex items-start gap-3 my-4 cursor-pointer">
          <input
            data-testid="signup-terms-checkbox"
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1 h-5 w-5 accent-[#E86A45]"
          />
          <span className="text-sm text-[#4A4746] leading-relaxed">
            I agree to the{" "}
            <Link to="/legal/terms" target="_blank" className="font-bold text-[#E86A45]">Terms</Link>,{" "}
            <Link to="/legal/privacy" target="_blank" className="font-bold text-[#E86A45]">Privacy Policy</Link> and{" "}
            <Link to="/legal/disclaimer" target="_blank" className="font-bold text-[#E86A45]">Medical Disclaimer</Link>.
          </span>
        </label>

        <PrimaryButton data-testid="signup-submit-btn" type="submit" loading={loading} className="w-full">
          Create account
        </PrimaryButton>
      </form>
    </AuthShell>
  );
}
